import type {
  DataObjectType,
  DataObjectRaw,
  DataObjectFieldsSchema,
  DataObjectFieldsSchemaRaw,
  DataObjectUISchema,
  DataObjectUISchemaRaw,
  DataObjectUISchemaFieldRaw,
  DataObjectFilterConfigRaw,
  DataObjectFieldRaw,
  DataObjectField,
  DataObjectUISchemaField,
  DataObjectChatConfigRangeConfig,
  DataObjectChatConfigRangeConfigRaw,
  DataObjectFilterConfig,
  DataObjectUISchemaSection,
  DataObjectUISchemaSectionRaw,
  DataObjectDetailUISchemaTab,
  DataObjectDetailUISchemaTabRaw,
  DataObjectFilterConfigSelectOption,
  DataObjectFilterConfigSelectOptionRaw,
  DataObjectDetailUISchemaRaw,
  DataObjectDetailUISchema,
  DataObjectWorkflowViewConfig,
  DataObjectWorkflowViewConfigRaw
} from "@certa/types/src/dataObjects.types";

/**
 * Forward parse x-rollup field from camelCase to snake_case with lossless transformation
 */
export const forwardParseXRollup = (
  xRollup: DataObjectField["x-rollup"]
): DataObjectFieldRaw["x-rollup"] => {
  if (!xRollup) return undefined;

  const {
    fromObjectType,
    forwardRelationName,
    sourceFieldPath,
    operationType,
    ...unknownProps
  } = xRollup;

  const result: DataObjectFieldRaw["x-rollup"] = {
    from_object_type: fromObjectType,
    forward_relation_name: forwardRelationName,
    source_field_path: sourceFieldPath,
    operation_type: operationType,
    ...unknownProps
  };

  return result;
};

/**
 * Forward parse x-relationship field from camelCase to snake_case with lossless transformation
 */
export const forwardParseXRelationship = (
  xRelationship: DataObjectField["x-relationship"]
): DataObjectFieldRaw["x-relationship"] => {
  if (!xRelationship) return undefined;

  const {
    toObjectType,
    relationshipType,
    reverseRelationName,
    ...unknownProps
  } = xRelationship;

  const result: DataObjectFieldRaw["x-relationship"] = {
    to_object_type: toObjectType,
    relationship_type: relationshipType,
    reverse_relation_name: reverseRelationName,
    ...unknownProps
  };

  return result;
};

/**
 * Forward parse a single DataObjectField from camelCase to snake_case with lossless transformation
 */
const forwardParseDataObjectField = (
  field: DataObjectFieldsSchema["properties"][string]
): DataObjectFieldRaw => {
  const {
    isIndexed,
    hasUniqueItems,
    "x-rollup": xRollup,
    "x-relationship": xRelationship,
    __isFrozen: isFrozen,
    ...restFieldProps
  } = field;

  const result: DataObjectFieldRaw = {
    ...restFieldProps
  };

  if (isIndexed !== undefined) {
    result.indexed = isIndexed;
  }

  if (hasUniqueItems !== undefined) {
    result.uniqueItems = hasUniqueItems;
  }

  if (xRollup) {
    result["x-rollup"] = forwardParseXRollup(xRollup);
  }

  if (xRelationship) {
    result["x-relationship"] = forwardParseXRelationship(xRelationship);
  }

  return result;
};

/**
 * Forward parse DataObjectFieldsSchema from camelCase to snake_case
 */
const forwardParseDataObjectSchema = (
  schema: DataObjectFieldsSchema
): DataObjectFieldsSchemaRaw => {
  const properties: DataObjectFieldsSchemaRaw["properties"] = {};

  for (const [fieldTag, field] of Object.entries(schema.properties)) {
    properties[fieldTag] = forwardParseDataObjectField(field);
  }

  return {
    properties,
    required: schema.required
  };
};

/**
 * Forward parse range config from camelCase to snake_case with lossless transformation
 */
const forwardParseRangeConfig = (
  rangeConfig: DataObjectChatConfigRangeConfig
): DataObjectChatConfigRangeConfigRaw => {
  const { maxRange, ranges, ...unknownProps } = rangeConfig;

  const result: DataObjectChatConfigRangeConfigRaw = {
    ...unknownProps
  };

  if (maxRange !== undefined) {
    result.max_range = maxRange;
  }

  if (ranges !== undefined) {
    result.ranges = ranges;
  }

  return result;
};

/**
 * Forward parse chart config from camelCase to snake_case with lossless transformation
 */
const forwardParseChartConfig = (
  chartConfig: DataObjectUISchemaField["chartConfig"]
): DataObjectUISchemaFieldRaw["chart_config"] => {
  if (!chartConfig) return undefined;

  const {
    identifierField,
    rangeConfig,
    dataPoints,
    xAxis,
    yAxis,
    ...unknownProps
  } = chartConfig;

  const result: DataObjectUISchemaFieldRaw["chart_config"] = {
    ...unknownProps
  };

  if (identifierField !== undefined) {
    result.identifier_field = identifierField;
  }

  if (rangeConfig) {
    result.range_config = forwardParseRangeConfig(rangeConfig);
  }

  if (dataPoints !== undefined) {
    result.data_points = dataPoints;
  }

  if (xAxis !== undefined) {
    result.x_axis = xAxis;
  }

  if (yAxis !== undefined) {
    result.y_axis = yAxis;
  }

  return result;
};

/**
 * Forward parse UI schema field from camelCase to snake_case with lossless transformation
 */
const forwardParseUISchemaField = (
  field: DataObjectUISchemaField
): DataObjectUISchemaFieldRaw => {
  const { chartConfig, ...unknownProps } = field;

  const result: DataObjectUISchemaFieldRaw = { ...unknownProps };

  if (chartConfig) {
    result.chart_config = forwardParseChartConfig(chartConfig);
  }

  return result;
};

/**
 * Forward parse section from camelCase to snake_case with lossless transformation
 */
const forwardParseSection = (
  section: DataObjectUISchemaSection
): DataObjectUISchemaSectionRaw => {
  const { displayName, ...unknownProps } = section;

  const result: DataObjectUISchemaSectionRaw = { ...unknownProps };

  if (displayName !== undefined) {
    result.display_name = displayName;
  }

  return result;
};

/**
 * Forward parse tab from camelCase to snake_case with lossless transformation
 */
const forwardParseTab = (
  tab: DataObjectDetailUISchemaTab
): DataObjectDetailUISchemaTabRaw => {
  const { initializerWorkflowKind, ...unknownProps } = tab;

  const result: DataObjectDetailUISchemaTabRaw = { ...unknownProps };

  if (initializerWorkflowKind !== undefined) {
    result.initializer_workflow_kind = initializerWorkflowKind;
  }

  return result;
};

/**
 * Forward parse workflows config from camelCase to snake_case with lossless transformation
 */
const forwardParseWorkflowsConfig = (
  workflows: DataObjectWorkflowViewConfig
): DataObjectWorkflowViewConfigRaw | undefined => {
  const { views, ...unknownProps } = workflows;

  let viewsResult: DataObjectWorkflowViewConfigRaw["views"];

  if (views) {
    const viewsObj = views;
    const { list, maxVisibleViewTabs, ...unknownViewProps } = viewsObj;

    viewsResult = { list: [], ...unknownViewProps };

    if (list) {
      viewsResult.list = list.map(({ kindTag, ...unknownProps }) => ({
        ...unknownProps,
        kind_tag: kindTag
      }));
    }

    if (maxVisibleViewTabs !== undefined) {
      viewsResult.max_visible_view_tabs = maxVisibleViewTabs;
    }
    return { views: viewsResult, ...unknownProps };
  }

  return undefined;
};

/**
 * Forward parse detail UI schema from camelCase to snake_case with lossless transformation
 */
const forwardParseDetailUISchema = (
  detail: DataObjectDetailUISchema
): DataObjectDetailUISchemaRaw => {
  const { sections, tabs, workflows, breadcrumbs, ...unknownProps } = detail;

  const result: DataObjectDetailUISchemaRaw = { ...unknownProps };

  if (sections) {
    result.sections = sections.map(forwardParseSection);
  }

  if (tabs) {
    result.tabs = tabs.map(forwardParseTab);
  }

  if (workflows) {
    result.workflows = forwardParseWorkflowsConfig(workflows);
  }

  if (breadcrumbs !== undefined) {
    result.breadcrumbs = breadcrumbs;
  }

  return result;
};

/**
 * Forward parse UI schema from camelCase to snake_case with lossless transformation
 */
const forwardParseUISchema = (
  uiSchema: DataObjectUISchema
): DataObjectUISchemaRaw => {
  const {
    displayNames,
    navigation,
    fields,
    list,
    detail,
    badges,
    ...unknownProps
  } = uiSchema;

  const result: DataObjectUISchemaRaw = { ...unknownProps };

  if (displayNames) {
    const { singular, plural, ...unknownDisplayNamesProps } = displayNames;
    result.display_names = {
      ...unknownDisplayNamesProps,
      singular,
      plural
    };
  }

  if (navigation) {
    const { isEnabled, order, ...unknownProps } = navigation;

    result.navigation = { ...unknownProps };

    if (isEnabled !== undefined) {
      result.navigation.enabled = isEnabled;
    }

    if (order !== undefined) {
      result.navigation.order = order;
    }
  }

  if (fields) {
    result.fields = {};
    for (const [fieldTag, field] of Object.entries(fields)) {
      result.fields[fieldTag] = forwardParseUISchemaField(field);
    }
  }

  if (list !== undefined) {
    result.list = list;
  }

  if (detail) {
    result.detail = forwardParseDetailUISchema(detail);
  }

  if (badges) {
    const { objectsTable, ...restBadges } = badges;
    const badgesResult: any = { ...restBadges };
    if (objectsTable) {
      const { isVisible, columnPosition, ...restObjectsTable } = objectsTable;
      badgesResult.objects_table = {
        ...restObjectsTable,
        ...(isVisible !== undefined && { is_visible: isVisible }),
        ...(columnPosition !== undefined && { column_position: columnPosition })
      };
    }
    result.badges = badgesResult;
  }

  return result;
};

/**
 * Forward parse select options from camelCase to snake_case with lossless transformation
 */
const transformSelectOptions = (
  selectOptions: DataObjectFilterConfigSelectOption
): DataObjectFilterConfigSelectOptionRaw => {
  const transformed: DataObjectFilterConfigSelectOptionRaw = {};

  for (const [key, value] of Object.entries(selectOptions)) {
    const { optionUrl, options, ...unknownProps } = value;

    transformed[key] = { ...unknownProps };

    if (optionUrl !== undefined) {
      transformed[key].option_url = optionUrl;
    }

    if (options !== undefined) {
      transformed[key].options = options;
    }
  }

  return transformed;
};

/**
 * Forward parse filter config from camelCase to snake_case with lossless transformation
 */
const forwardParseFilterConfig = (
  filterConfig: DataObjectFilterConfig
): DataObjectFilterConfigRaw => {
  if (!filterConfig) return {};

  const { multiSelect, singleSelect, ...unknownProps } = filterConfig;

  const result: DataObjectFilterConfigRaw = { ...unknownProps };

  if (multiSelect) {
    result.multi_select = transformSelectOptions(multiSelect);
  }

  if (singleSelect) {
    result.single_select = transformSelectOptions(singleSelect);
  }

  return result;
};

/**
 * Forward parse DataObjectType to DataObjectRaw
 * Converts known properties from camelCase to snake_case while preserving unknown properties
 * for lossless transformation
 */
export const forwardParseDataObject = (
  dataObject: DataObjectType
): DataObjectRaw => {
  const {
    tag,
    schema,
    uiSchema,
    translatedUiSchema,
    filterConfig,
    __saveId, // Exclude metadata
    __description,
    ...unknownProps // Preserve any unknown properties
  } = dataObject;

  const result: DataObjectRaw = {
    tag,
    schema: forwardParseDataObjectSchema(schema),
    ui_schema: forwardParseUISchema(uiSchema),
    translated_ui_schema: translatedUiSchema
      ? forwardParseUISchema(translatedUiSchema)
      : undefined,
    filter_config: forwardParseFilterConfig(filterConfig),
    // Pass through any unknown properties as-is for lossless transformation
    ...unknownProps
  };

  return result;
};
